import type { LocalOperation } from "./protocol";

export const LEGACY_WORKSPACE_ID = "local-workspace-v1";
export type LocalAccount = { project: string; userId: string; email: string; workspaceId: string };

export function localAccount(projectUrl: string, userId: string, email: string): LocalAccount {
  const project = new URL(projectUrl).hostname;
  if (!userId.trim() || userId.length > 256) throw new Error("帳戶識別無效。");
  return { project, userId, email, workspaceId: `account:${project}:${encodeURIComponent(userId)}` };
}

export function assertAccountSession(account: LocalAccount) {
  const key = `sb-${account.project.split(".")[0]}-auth-token`;
  const saved = localStorage.getItem(key);
  if (!saved) return; // Only an already-open, same-account recovery view may reach this path.
  const identity = JSON.parse(saved)?.user?.id;
  if (identity !== account.userId) throw new Error("登入帳戶已變更，請重新開啟目前帳戶的資料。");
}

function leaseKey(account: LocalAccount) {
  return `classroom-account-session:${account.project}`;
}
export function accountEpoch(account: LocalAccount): number {
  assertAccountSession(account);
  const lease = JSON.parse(localStorage.getItem(leaseKey(account)) || "null");
  if (lease?.workspaceId !== account.workspaceId || !Number.isSafeInteger(lease.revision))
    throw new Error("登入帳戶已變更，請重新開啟目前帳戶的資料。");
  return lease.revision;
}
export async function beginAccountSession(account: LocalAccount): Promise<number> {
  return navigator.locks.request("classroom-account-session", () => {
    assertAccountSession(account);
    const previous = JSON.parse(localStorage.getItem(leaseKey(account)) || "null");
    if (previous?.workspaceId === account.workspaceId && Number.isSafeInteger(previous.revision))
      return previous.revision;
    const revision = Math.max(Date.now(), Number(previous?.revision || 0) + 1);
    localStorage.setItem(leaseKey(account), JSON.stringify({ workspaceId: account.workspaceId, revision }));
    return revision;
  });
}

export function assertOperationScope(operation: LocalOperation, account?: LocalAccount) {
  const workspaceId =
    "workspaceId" in operation ? operation.workspaceId : "workspace" in operation ? operation.workspace.id : undefined;
  if (account) {
    if (localAccount(`https://${account.project}`, account.userId, account.email).workspaceId !== account.workspaceId)
      throw new Error("帳戶工作區識別無效。");
    if (workspaceId && workspaceId !== account.workspaceId) throw new Error("不能存取其他帳戶的本機資料。");
    if (
      operation.kind === "claimLegacyWorkspace" &&
      operation.email.trim().toLowerCase() !== account.email.trim().toLowerCase()
    )
      throw new Error("請以舊資料擁有者的 Google 帳戶登入，再確認電郵。");
  } else if (
    workspaceId?.startsWith("account:") ||
    ["accountStatus", "claimLegacyWorkspace", "deferLegacyWorkspace"].includes(operation.kind)
  ) {
    throw new Error("請先登入資料所屬帳戶。");
  }
}
